import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import QuickFab from "@/components/QuickFab";
import UpgradeProModal from "@/components/UpgradeProModal";
import {
  LayoutDashboard, Sparkles, FileText, Users, LayoutTemplate,
  CreditCard, ShieldCheck, LogOut, User as UserIcon, Menu, X, Gift, Wallet, Receipt, FolderOpen, Smartphone, Camera
} from "lucide-react";

const linkBase = "flex items-center gap-3 px-3 h-11 text-sm font-medium transition-colors no-tap";
const inactive = "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
const active = "bg-slate-900 text-white";

function NavItem({ to, icon: Icon, label, testid, end = false, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      onClick={onClick}
      className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const close = () => setOpen(false);

  const NavList = (
    <nav className="flex flex-col gap-1 p-3">
      <div className="kbar px-3 py-3">Principal</div>
      <NavItem to="/dashboard" icon={LayoutDashboard} label="Panel" testid="nav-dashboard" onClick={close} />
      <NavItem to="/generator" icon={Sparkles} label="Generar con IA" testid="nav-generator" onClick={close} />
      <NavItem to="/gasto-rapido" icon={Camera} label="Gasto Rápido" testid="nav-quick-expense" onClick={close} />
      <NavItem to="/budgets" icon={FileText} label="Presupuestos" testid="nav-budgets" onClick={close} />
      <NavItem to="/cashflow" icon={Wallet} label="Mis ingresos" testid="nav-cashflow" onClick={close} />
      <NavItem to="/accounting" icon={Receipt} label="Contabilidad" testid="nav-accounting" onClick={close} />
      <NavItem to="/clients" icon={Users} label="Clientes" testid="nav-clients" onClick={close} />
      <NavItem to="/files" icon={FolderOpen} label="Facturas y BC3" testid="nav-files" onClick={close} />
      <NavItem to="/templates" icon={LayoutTemplate} label="Plantillas" testid="nav-templates" onClick={close} />
      <div className="kbar px-3 py-3 mt-3">Cuenta</div>
      <NavItem to="/profile" icon={UserIcon} label="Mi perfil" testid="nav-profile" onClick={close} />
      <NavItem to="/billing" icon={CreditCard} label="Suscripción" testid="nav-billing" onClick={close} />
      <NavItem to="/referrals" icon={Gift} label="Invita y gana" testid="nav-referrals" onClick={close} />
      <NavItem to="/instalar" icon={Smartphone} label="Instalar en móvil" testid="nav-install" onClick={close} />
      {user?.role === "admin" && (
        <NavItem to="/admin" icon={ShieldCheck} label="Admin" testid="nav-admin" onClick={close} />
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar (mobile) */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-white border-b border-slate-200 h-14 px-4">
        <button data-testid="menu-toggle" className="p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <span className="font-display font-extrabold tracking-tight text-lg">AutoPresupuestos<span className="text-orange-600">.</span></span>
        <button data-testid="logout-mobile" onClick={logout} className="p-2"><LogOut className="w-5 h-5" /></button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-30 w-72 bg-white border-r border-slate-200 transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"} lg:h-screen`}
        >
          <div className="h-16 flex items-center px-5 border-b border-slate-200">
            <span className="font-display font-extrabold tracking-tight text-xl">AutoPresupuestos<span className="text-orange-600">.</span></span>
          </div>

          {NavList}

          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 bg-white">
            <div className="px-3 py-2">
              <div className="text-xs text-slate-500">Sesión iniciada</div>
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.full_name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            {user?.subscription_status === "trialing" && (
              <div className="mx-3 my-2 px-3 py-2 bg-orange-50 border border-orange-200 text-orange-900 text-xs">
                <div className="font-bold">Prueba gratuita</div>
                <div>{trialDays} días restantes</div>
              </div>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className={`${linkBase} ${inactive} w-full text-left`}
            >
              <LogOut className="w-5 h-5" /> Cerrar sesión
            </button>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-20 bg-black/30 lg:hidden" onClick={close} />}

        <main className="flex-1 min-w-0">
          <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
            <Outlet />
          </div>
          <InstallPrompt />
          <QuickFab />
          <UpgradeProModal />
        </main>
      </div>
    </div>
  );
}
